/**
 * Worked For Group
 *
 * Collapses a consecutive run of tools + thoughts (no intervening answer text)
 * into one "Worked for n seconds" dropdown with nested sub-rows.
 *
 * Primary responsibilities:
 * - Summarize pre-text / between-text work bunches
 * - Nest Thought + tool rows inside the group
 * - Stay open while the bunch is still live
 *
 * Dependencies:
 * - Reasoning
 * - ToolTimeline / ToolActivityLog
 */

'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Timer } from 'lucide-react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning'
import { ToolTimeline } from '@/components/messages/annotations/tool-timeline'
import { ToolActivityLog } from '@/components/messages/annotations/tool-activity-log'
import { LiveScoreAnnotation } from '@/components/messages/annotations/live-score'
import { cn } from '@/lib/utils'
import {
  estimateGroupDurationMs,
  getWorkedForLabel,
  isLiveWorkGroup,
  type WorkedForItem
} from '@/lib/chat/worked-for-blocks'
import { isMediaGenerationToolKey } from '@/components/messages/annotations/tool-results'

function WorkedForItemRow({ item }: { item: WorkedForItem }) {
  if (item.kind === 'reasoning') {
    const durationSeconds =
      typeof item.durationMs === 'number'
        ? Math.max(0, Math.round(item.durationMs / 1000))
        : undefined

    return (
      <Reasoning
        className="w-full"
        isStreaming={Boolean(item.isLive)}
        duration={durationSeconds}
        defaultOpen={Boolean(item.isLive)}
        autoCloseOnEnd={!item.isLive}
      >
        <ReasoningTrigger />
        <ReasoningContent animateStreaming={false}>{item.text}</ReasoningContent>
      </Reasoning>
    )
  }

  if (item.entry) {
    return <ToolTimeline entries={[item.entry]} className="mb-0" />
  }

  if (item.activityAnnotation) {
    const liveScoreKey = Object.keys(item.activityAnnotation).find(key =>
      key.startsWith('liveScores__')
    )
    if (liveScoreKey) {
      return (
        <LiveScoreAnnotation
          annotation={item.activityAnnotation as never}
          toolKey={liveScoreKey}
        />
      )
    }
    return <ToolActivityLog annotation={item.activityAnnotation} className="mb-0" />
  }

  return null
}

export function WorkedForGroup({
  items,
  isGenerating = false,
  className,
  defaultOpen
}: {
  items: WorkedForItem[]
  isGenerating?: boolean
  className?: string
  defaultOpen?: boolean
}) {
  const isLive = isLiveWorkGroup(items, isGenerating)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const wasLiveRef = useRef(isLive)

  const visibleItems = useMemo(
    () =>
      items.filter(item => {
        if (item.kind === 'reasoning') return Boolean(item.text.trim())
        return Boolean(item.entry || item.activityAnnotation)
      }),
    [items]
  )

  // Image/video previews must stay visible — don't bury them in a collapsed group.
  const hasMediaTools = useMemo(
    () =>
      visibleItems.some(
        item =>
          item.kind === 'tool' &&
          typeof item.entry?.name === 'string' &&
          isMediaGenerationToolKey(item.entry.name)
      ),
    [visibleItems]
  )

  const [isOpen, setIsOpen] = useState(
    () => defaultOpen ?? (isLive || hasMediaTools)
  )

  useEffect(() => {
    if (!isLive) return
    const tick = () => setNowMs(Date.now())
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [isLive])

  useEffect(() => {
    if (isLive) {
      setIsOpen(true)
      wasLiveRef.current = true
      return
    }
    // After a live run finishes, collapse so the answer text leads —
    // except when the bunch includes generated media (keep previews rendered).
    if (wasLiveRef.current && defaultOpen == null && !hasMediaTools) {
      wasLiveRef.current = false
      setIsOpen(false)
    }
  }, [isLive, defaultOpen, hasMediaTools])

  const durationMs = useMemo(
    () => estimateGroupDurationMs(items, nowMs),
    [items, nowMs]
  )
  const label = getWorkedForLabel({ durationMs, isLive })

  if (visibleItems.length === 0) return null

  // One tool or one thought — keep the native row (no double dropdown).
  // Media tools also skip the WorkedFor wrapper so the preview leads.
  if (visibleItems.length === 1 || (hasMediaTools && visibleItems.every(item => item.kind === 'tool'))) {
    return (
      <div className={cn('w-full space-y-2', className)}>
        {visibleItems.map(item => (
          <WorkedForItemRow key={item.id} item={item} />
        ))}
      </div>
    )
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn('not-prose group/worked-for w-full', className)}
    >
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-2 py-1 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2">
        <Timer className={cn('size-3.5 shrink-0', isLive && 'text-primary')} />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown className="size-3.5 shrink-0 transition-transform group-data-[state=open]/worked-for:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <div className="border-border/50 flex flex-col gap-1 border-l py-1 pl-3">
          {visibleItems.map(item => (
            <WorkedForItemRow key={item.id} item={item} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
